/**
 * Aguardando reabertura: o que chegou depois de o mês ter sido fechado, e o botão de reabrir.
 *
 * **Esta é a primeira tela do painel que ESCREVE.** Todas as outras só mostram. Por isso ela é
 * desenhada ao contrário das demais: o que pode mudar o dinheiro fica atrás de dois passos
 * (abrir o grupo, depois confirmar com motivo e senha), em vez de a um clique de distância.
 *
 * **Reabrir não refecha**, e a tela diz isso com essas palavras ANTES do botão. Assinar o mês
 * de novo continua sendo no notebook, que é quem calcula os totais do retrato. É a decisão 6 da
 * parte 3, e é a coisa que o pastor mais precisa entender aqui: ele vai reabrir, e depois
 * alguém vai ter que fechar de novo lá.
 *
 * **A lista não leva nome de dizimista.** O resumo vem pronto do servidor, "Dízimo de
 * R$ 1.000,00 em 01/08/2026", sem a pessoa. Lista de pendência é tela de gestão; quem tem
 * permissão vê o nome na folha da congregação.
 */
import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type AEspera, type Eu, type GrupoEsperando, type ReaberturaFeita
} from '../api'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { competenciaPorExtenso } from '../dinheiro'

/** `2026-08-05T12:00:00Z` vira `05/08/2026`. */
function dia(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

/** A chave que identifica um grupo na tela: o mesmo trio que o servidor usa. */
const chaveDo = (g: GrupoEsperando): string =>
  `${g.tipo_de_fechamento}|${g.congregacao.id ?? ''}|${g.competencia}`

const nomeDoGrupo = (g: GrupoEsperando): string =>
  g.tipo_de_fechamento === 'CAMPO'
    ? 'Fechamento do Campo'
    : g.congregacao.nome ?? 'Congregação não identificada'

export function Aguardando({ eu, aoSair }: { eu: Eu; aoSair: () => void }): JSX.Element {
  const [dados, setDados] = useState<AEspera | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [abertoAgora, setAbertoAgora] = useState<string | null>(null)
  const [feito, setFeito] = useState<ReaberturaFeita | null>(null)

  const carregar = useCallback(() => {
    setErro(null)
    servidor
      .aguardando()
      .then(setDados)
      .catch((falha: unknown) => {
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })
  }, [aoSair])

  useEffect(carregar, [carregar])

  /**
   * Quantos lançamentos estão esperando, somando todos os grupos.
   *
   * Serve só para as frases da tela. Com um lançamento só, escrever "estes lançamentos" e "eles"
   * faz a tela falar de um punhado que não existe. Achado TEX-002 da auditoria, 13/10/2026.
   */
  const quantosEsperam =
    dados === null ? 0 : dados.grupos.reduce((soma, g) => soma + g.quantidade, 0)

  return (
    <>
      <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: '/', texto: 'Início' }} />

      <main className="folha" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        <h1>Aguardando reabertura</h1>

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {feito !== null && (
          <p className="recado feito" role="status">{feito.recado}</p>
        )}

        {dados !== null && dados.grupos.length === 0 && (
          <p className="recado nada">
            Nada esperando. Todo lançamento que chegou entrou no mês dele.
          </p>
        )}

        {dados !== null && dados.grupos.length > 0 && (
          <>
            <p className="explicacao">
              {quantosEsperam === 1 ? (
                <>
                  Este lançamento chegou depois de o mês ter sido fechado e assinado.{' '}
                  <strong>Nada se perdeu</strong>: ele está guardado aqui e entra sozinho assim
                  que o mês for reaberto.
                </>
              ) : (
                <>
                  Estes lançamentos chegaram depois de o mês ter sido fechado e assinado.{' '}
                  <strong>Nada se perdeu</strong>: eles estão guardados aqui e entram sozinhos
                  assim que o mês for reaberto.
                </>
              )}
            </p>

            {dados.grupos.map((grupo) => (
              <GrupoDaEspera
                key={chaveDo(grupo)}
                grupo={grupo}
                aberto={abertoAgora === chaveDo(grupo)}
                aoAbrir={() => setAbertoAgora(abertoAgora === chaveDo(grupo) ? null : chaveDo(grupo))}
                aoReabrir={(resultado) => {
                  setFeito(resultado)
                  setAbertoAgora(null)
                  carregar()
                }}
                aoSair={aoSair}
              />
            ))}
          </>
        )}
      </main>
    </>
  )
}

/**
 * Um mês preso, com os lançamentos dele e o caminho de reabrir.
 *
 * A lista dos lançamentos fica fechada até alguém clicar. Um Campo com dezenas de lançamentos
 * presos viraria uma parede de texto, e o que o pastor precisa ver primeiro é QUAL mês de QUEM
 * está preso, e quantos são.
 */
function GrupoDaEspera({
  grupo,
  aberto,
  aoAbrir,
  aoReabrir,
  aoSair
}: {
  grupo: GrupoEsperando
  aberto: boolean
  aoAbrir: () => void
  aoReabrir: (resultado: ReaberturaFeita) => void
  aoSair: () => void
}): JSX.Element {
  const [confirmando, setConfirmando] = useState(false)

  return (
    <section className="cartao-da-espera">
      <header>
        <button className="abrir-grupo" onClick={aoAbrir} aria-expanded={aberto}>
          <span className="nome">
            {nomeDoGrupo(grupo)}
            <span className="situacao FECHADO">fechado e assinado</span>
          </span>
          <span className="detalhe">
            {competenciaPorExtenso(grupo.competencia)} ·{' '}
            {grupo.quantidade === 1 ? '1 lançamento esperando' : `${grupo.quantidade} lançamentos esperando`}
            {grupo.fechamento !== null && grupo.fechamento.assinado_em !== null && (
              <> · assinado em {dia(grupo.fechamento.assinado_em)}</>
            )}
          </span>
          <span className="seta">{aberto ? '▲' : '▼'}</span>
        </button>
      </header>

      {aberto && (
        <>
          <ul className="lancamentos-esperando">
            {grupo.lancamentos.map((l) => (
              <li key={l.evento_id}>
                <span className="resumo">{l.resumo}</span>
                <span className="quando">
                  lançado por {l.usuario_nome} · chegou em {dia(l.recebido_em)}
                </span>
              </li>
            ))}
          </ul>

          {/*
            O aviso antes do botão, e não depois. Quem lê de cima para baixo encontra o que
            acontece ANTES de decidir, que é a única ordem que serve para alguma coisa.
          */}
          <p className="explicacao atencao">
            Reabrir <strong>não refecha</strong>. O mês volta a aceitar lançamentos, e{' '}
            {grupo.quantidade === 1
              ? 'o que está esperando entra'
              : 'os que estão esperando entram'}{' '}
            na hora. Depois alguém precisa <strong>assinar de novo no notebook</strong>, que é
            quem calcula os totais do papel.
            {grupo.tipo_de_fechamento === 'CONGREGACAO'
              ? ' O repasse que ainda estava só no papel é cancelado e recalculado no refechamento; o que já foi aprovado ou pago fica como está.'
              : ' As congregações travadas neste mês são destravadas.'}
          </p>

          {confirmando ? (
            <FormularioDeReabrir
              grupo={grupo}
              aoDesistir={() => setConfirmando(false)}
              aoReabrir={aoReabrir}
              aoSair={aoSair}
            />
          ) : (
            <button className="perigo" onClick={() => setConfirmando(true)}>
              Reabrir {competenciaPorExtenso(grupo.competencia)} e aplicar
            </button>
          )}
        </>
      )}
    </section>
  )
}

/** O motivo e a senha. Os dois são obrigatórios, e o motivo fica no papel e na trilha. */
function FormularioDeReabrir({
  grupo,
  aoDesistir,
  aoReabrir,
  aoSair
}: {
  grupo: GrupoEsperando
  aoDesistir: () => void
  aoReabrir: (resultado: ReaberturaFeita) => void
  aoSair: () => void
}): JSX.Element {
  const [motivo, setMotivo] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  async function reabrir(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)

    try {
      const resultado = await servidor.reabrir({
        tipo: grupo.tipo_de_fechamento === 'CAMPO' ? 'CAMPO' : 'CONGREGACAO',
        congregacao_id: grupo.congregacao.id,
        competencia: grupo.competencia,
        motivo,
        senha
      })
      aoReabrir(resultado)
    } catch (falha: unknown) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível reabrir.')
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-reabrir" onSubmit={(e) => void reabrir(e)}>
      {/*
        A frase fica AQUI, na hora de confirmar, e não só no aviso de cima.

        Pedido do Cristiano ao aprovar a entrega 4. O aviso de cima explica o que vai acontecer;
        esta linha fica onde a pessoa está decidindo, com o dedo no botão. Quem rola a tela para
        preencher o motivo já passou do aviso.
      */}
      <p className="recado atencao" role="note">
        Reabrir aqui não fecha de novo. Assinar o mês outra vez é no notebook.
      </p>

      <label>
        Por que este mês está sendo reaberto?
        <input
          className="campo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Chegou lançamento atrasado da tesouraria"
          maxLength={500}
          required
        />
      </label>
      <p className="explicacao">Este texto fica guardado no fechamento e na trilha de auditoria.</p>

      <label>
        Sua senha
        <input
          className="campo"
          type="password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          autoComplete="current-password"
          required
        />
      </label>
      {/* Dizer POR QUE a senha é pedida de novo. Sem isso parece defeito ou desconfiança. */}
      <p className="explicacao">
        A senha é pedida de novo porque esta ação desfaz uma assinatura. É a mesma regra do
        notebook.
      </p>

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <div className="linha-form">
        <button type="submit" className="perigo" disabled={indo || motivo.trim().length < 3 || senha.length === 0}>
          {indo ? 'Reabrindo...' : 'Reabrir e aplicar'}
        </button>
        <button type="button" onClick={aoDesistir} disabled={indo}>
          Deixar como está
        </button>
      </div>
    </form>
  )
}
